/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import debounce from "lodash/debounce";

import { Typography } from "@mui/material";

const MenusPicker = ({
  menus = {},
  menusV = [],
  handleChange = () => {},
  setFieldValue,
  locationId = "",
}) => {
  const filterRec = (data) =>
    data.map((menuData) => ({
      label: menuData.title ? menuData.title : menuData.label,
      value: menuData._id ? menuData._id : menuData.value,
      icon: menuData.icon && menuData.icon,
    }));

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      let url = `${process.env.REACT_APP_API}/menus/wifiMenus?location=${locationId}`;
      if (inputValue && inputValue?.length > 0) url += `&title=${inputValue}`;
      url += "&sort=title";
      setTimeout(() => {
        fetch(url)
          .then((response) => response.json())
          .then((result) => {
            const filteredData = filterRec(result.data);
            // Sort the filtered data, placing special characters at the bottom
            const sortedData = filteredData.sort((a, b) => {
              const isSpecialCharA = /[^a-zA-Z0-9]/.test(a.label[0]);
              const isSpecialCharB = /[^a-zA-Z0-9]/.test(b.label[0]);
              if (isSpecialCharA && !isSpecialCharB) return 1;
              if (!isSpecialCharA && isSpecialCharB) return -1;
              return a.label.localeCompare(b.label);
            });
            resolve(sortedData);
          })
          .catch((error) => console.log(error.message));
      }, 1000);
    });

  useEffect(() => {
    promiseOptions();
  }, []);

  const debouncedLoadOptions = debounce((inputValue, callback) => {
    promiseOptions(inputValue)
      .then((options) => {
        callback(options);
      })
      .catch((error) => {
        console.error(error);
      });
  }, 1000); // Adjust the delay time (in milliseconds) as needed

  const handleChangeWrapper = (options) => {
    if (!options || options.length === 0) {
      setFieldValue("menus", []);
      handleChange(options);
    } else {
      const formattedOptions = options.map((option) => ({
        value: option._id || option.value,
        label: option.title || option.label,
        icon: option.icon,
      }));

      setFieldValue("menus", formattedOptions);
      handleChange(formattedOptions);
    }
  };

  return (
    <div>
      <Typography
        style={{
          wordBreak: "break-word",
          fontSize: "0.875rem",
          fontWeight: 400,
        }}
      >
        {menus.label}
      </Typography>{" "}
      <AsyncSelect
        isMulti
        cacheOptions
        defaultOptions
        value={menusV?.map((tag) => ({
          label: tag.title || tag.label,
          value: tag._id || tag.value,
        }))}
        placeholder="Search.."
        menuPosition="fixed"
        loadOptions={debouncedLoadOptions}
        onChange={handleChangeWrapper}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          LoadingIndicator: () => null,
        }}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: "40px",
            borderColor: state.isFocused ? "#003005" : base.borderColor, // Change border color on focus
            boxShadow: state.isFocused ? "0 0 0 1px #003005" : base.boxShadow, // Optional: Add a shadow for better visual
            "&:hover": {
              borderColor: state.isFocused ? "#003005" : base.borderColor, // Maintain border color on hover
            },
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          input: (base) => ({
            ...base,
            fontSize: "0.875rem",
          }),
          menuList: (base) => ({
            ...base,
            fontSize: "0.875rem",
          }),
          placeholder: (base) => ({
            ...base,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "100%",
            WebkitLineClamp: 1,
            fontSize: "0.875rem",
          }),
          multiValue: (base) => ({
            ...base,
            fontSize: "0.875rem",
          }),
        }}
      />
    </div>
  );
};

MenusPicker.propTypes = {
  menus: PropTypes.instanceOf(Object),
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  menusV: PropTypes.instanceOf(Array),
  locationId: PropTypes.string,
};

export default MenusPicker;
